/**
 * Panel general: cifras del día de la sucursal, según permisos. El cajero no ve aquí lo recaudado en su caja: cuenta a
 * ciegas al cerrar (spec 11, enmienda 2026-10-09).
 */
import { Money, type ReporteVentasDTO, type ResumenRetencionesDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import { Link } from 'react-router';
import { Vacio } from '@/componentes/estados.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { ETIQUETA_ROL } from '@/lib/utils.ts';
import { AvisoCajaOtraSucursal } from '@/modulos/caja/AbrirCaja.tsx';

const hoy = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Caracas' });

function Cifra({ titulo, valor, sub, ruta, cargando }: { titulo: string; valor: string; sub?: string | undefined; ruta: string; cargando?: boolean | undefined }) {
  return (
    <Card>
      <CardHeader>
        <CardDescription>{titulo}</CardDescription>
        <CardTitle className="num text-2xl">{cargando ? '…' : valor}</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{sub ?? ''}</span>
        <Link to={ruta} className="text-mg-verde hover:underline">
          Ver
        </Link>
      </CardContent>
    </Card>
  );
}

export function Panel() {
  const { usuario, puede } = useAuth();
  const enSucursal = Boolean(usuario?.sucursal_id);
  const veVentas = enSucursal && puede('reportes.sucursal', 'lectura');
  const veEntregas = enSucursal && puede('inventario.entregas', 'lectura');
  const veRetenciones = enSucursal && puede('retenciones-clientes.cargar', 'lectura');
  const fecha = hoy();
  const ventas = useQuery({ queryKey: ['panel', 'ventas', fecha], queryFn: () => get<ReporteVentasDTO>('/reportes/ventas', { desde: fecha, hasta: fecha }), enabled: veVentas });
  const entregas = useQuery({ queryKey: ['panel', 'entregas'], queryFn: () => get<{ total?: number; datos?: unknown[] }>('/inventario/pendientes', { limite: 1 }), enabled: veEntregas });
  const retenciones = useQuery({ queryKey: ['panel', 'retenciones'], queryFn: () => get<ResumenRetencionesDTO>('/retenciones/resumen'), enabled: veRetenciones });
  if (!usuario) return null;

  const sinCaja = usuario.rol === 'cajero' && !usuario.sesion_caja;
  return (
    <>
      <TituloPagina titulo={`Panel general · ${usuario.sucursal_nombre ?? 'Global'}`} subtitulo={`Sesión de ${usuario.nombre} · ${ETIQUETA_ROL[usuario.rol]}`} />
      {sinCaja ? (
        <Vacio
          icono={<Wallet className="h-6 w-6" />}
          titulo="Aún no tienes caja abierta"
          descripcion="Para cobrar necesitas abrir tu sesión de caja declarando el fondo inicial en $ y Bs."
          accion={
            <Button asChild>
              <Link to="/caja">Abrir caja</Link>
            </Button>
          }
        />
      ) : null}
      {usuario.caja_en_otra_sucursal ? <div className="mb-4"><AvisoCajaOtraSucursal /></div> : null}
      {usuario.sesion_caja ? (
        <Card className="mb-4" data-testid="panel-caja-abierta">
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <CardTitle>{usuario.sesion_caja.caja_nombre} abierta</CardTitle>
              <CardDescription>{usuario.sesion_caja.declarado ? 'Ya declaraste tu conteo: falta terminar el cierre.' : 'Al terminar el turno, cuenta lo que tienes y cierra la caja.'}</CardDescription>
            </div>
            <Button asChild variant="outline">
              <Link to="/caja">Ir a Caja</Link>
            </Button>
          </CardHeader>
        </Card>
      ) : null}
      {veVentas || veEntregas || veRetenciones ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {veVentas ? (
            <Cifra
              titulo="Ventas de hoy"
              valor={ventas.data ? `$ ${Money.parse(ventas.data.total).toDisplay()}` : '—'}
              sub={ventas.data ? `${ventas.data.documentos} documentos · fiscal $ ${Money.parse(ventas.data.total_fiscal).toDisplay()}` : undefined}
              ruta="/reportes"
              cargando={ventas.isLoading}
            />
          ) : null}
          {veEntregas ? (
            <Cifra titulo="Por entregar" valor={entregas.data ? String(entregas.data.total ?? entregas.data.datos?.length ?? 0) : '—'} sub="documentos con mercancía pendiente" ruta="/entregas" cargando={entregas.isLoading} />
          ) : null}
          {veRetenciones ? (
            <Cifra
              titulo="Retenciones por recibir"
              valor={retenciones.data ? String(retenciones.data.por_recibir.cantidad) : '—'}
              sub={retenciones.data ? `$ ${Money.parse(retenciones.data.por_recibir.monto).toDisplay()} · ${retenciones.data.vencidas_30} con más de 30 días` : undefined}
              ruta="/retenciones"
              cargando={retenciones.isLoading}
            />
          ) : null}
        </div>
      ) : null}
    </>
  );
}
